import React, { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Paper, Typography, IconButton } from '@mui/material';
import { Cancel, CheckCircle, Upload, AccountTree, DeleteOutline, Close } from '@mui/icons-material';
import { useAtom } from 'jotai';

import { ErrorBox } from './ErrorBox';
import { UploadBox } from '../upload/upload-box';
import { supabase } from '../../../supabaseClient';

import { getFileNameWithoutExtension } from '../../utils/common/stringUtils';
import { showToastError, showToastSuccess } from '../../utils/common/toast';
import { mapExtensionToAllowedContentType } from '../../utils/constants/contentTypes';
import { BUCKET_MEDIA_PATH } from '../../utils/constants/sharedConstants';
import { validateDrawioZip } from '../../utils/parsing/zipFileUtils';
import {
    deleteMediaInBucket,
    getCatalogGraph,
    clearCatalog,
    indexCatalogNodes
} from '../../utils/server/storageUtils';
import { uploadingDataState } from '../../state/chat/UploadingDataState';
import { useAppTranslation } from '../../hooks/useAppTranslation';
import { getAuthToken } from '../../utils/client/getAuthToken';
import { getActiveChatbotId, getActiveTenantId } from '../../utils/server/authUtils';

function isValidUuid(value) {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
        String(value || '').trim()
    );
}

function sanitizeChatbotId(value = null) {
    return isValidUuid(value) ? String(value).trim() : null;
}

async function resolveActiveScope() {
    const token = await getAuthToken();
    const tenantId = getActiveTenantId();
    const chatbotId = sanitizeChatbotId(getActiveChatbotId());

    return { token, tenantId, chatbotId };
}

export function DrawioUploadPanel() {
    const { t, tr } = useAppTranslation();
    const acceptedFileTypes = ['.zip'];

    const [selectedFile, setSelectedFile] = useState();
    const [currentErrors, setCurrentErrors] = useState([]);
    const [currentWarnings, setCurrentWarnings] = useState([]);
    const [isLoading, setIsLoading] = useAtom(uploadingDataState);
    const [stagedStats, setStagedStats] = useState(null);

    const [catalogStats, setCatalogStats] = useState({ nodeCount: 0 });
    const [isLoadingStats, setIsLoadingStats] = useState(false);
    const [isClearing, setIsClearing] = useState(false);

    const loadCatalogStats = React.useCallback(async () => {
        setIsLoadingStats(true);
        try {
            const { token, tenantId, chatbotId } = await resolveActiveScope();
            if (!token || !tenantId || !chatbotId) return;

            const data = await getCatalogGraph(token, chatbotId, tenantId);
            setCatalogStats({ nodeCount: data?.nodes?.length || 0 });
        } catch (error) {
            console.error('Failed to load catalog stats', error);
        } finally {
            setIsLoadingStats(false);
        }
    }, []);

    const clearSelectedFile = () => {
        setSelectedFile(undefined);
        setCurrentErrors([]);
        setCurrentWarnings([]);
        setStagedStats(null);
    };

    useEffect(() => {
        loadCatalogStats();

        const handleScopeChange = () => {
            if (!isLoading) {
                clearSelectedFile();
            }
            loadCatalogStats();
        };

        window.addEventListener('active-chatbot-changed', handleScopeChange);
        window.addEventListener('active-tenant-changed', handleScopeChange);

        return () => {
            window.removeEventListener('active-chatbot-changed', handleScopeChange);
            window.removeEventListener('active-tenant-changed', handleScopeChange);
        };
    }, [isLoading, loadCatalogStats]);

    const handleClearDrawio = async () => {
        if (isClearing) return;

        setIsClearing(true);
        try {
            const { token, tenantId, chatbotId } = await resolveActiveScope();
            if (!token || !tenantId || !chatbotId) {
                showToastError(t.drawioMissingUploadContext);
                return;
            }

            const cleared = await clearCatalog(token, chatbotId, tenantId);
            if (cleared) {
                await deleteMediaInBucket(token, [], chatbotId, tenantId);
                showToastSuccess(t.drawioClearSuccess);
                await loadCatalogStats();
            } else {
                showToastError(t.drawioClearFailed);
            }
        } catch (error) {
            console.error(error);
            showToastError(t.drawioClearError);
        } finally {
            setIsClearing(false);
        }
    };

    const onDropAccepted = async (acceptedFiles) => {
        if (acceptedFiles.length !== 1) return;

        const file = acceptedFiles[0];
        const fileType = String(file.name.split('.').pop() || '').toLowerCase();

        if (!acceptedFileTypes.includes(`.${fileType}`)) {
            showToastError(t.errorInvalidFileType);
            return;
        }

        try {
            const validatedZipFile = await validateDrawioZip(file);
            setCurrentErrors(validatedZipFile.errors ?? []);
            setCurrentWarnings(validatedZipFile.warnings ?? []);
            setSelectedFile(file);

            if (!validatedZipFile.errors || validatedZipFile.errors.length === 0) {
                setStagedStats({
                    nodes: validatedZipFile.catalog?.nodes?.length || 0,
                    media: validatedZipFile.mediaFiles?.length || 0
                });
            } else {
                setStagedStats(null);
            }
        } catch (error) {
            console.error(error);
            clearSelectedFile();
            showToastError(t.errorUploadFailed);
        }
    };

    const handleUpload = async () => {
        if (isLoading) {
            showToastError(t.errorUploadInProgress);
            return;
        }

        if (!selectedFile) {
            showToastError(t.errorSelectValidFile);
            return;
        }

        if (currentErrors.length > 0) {
            showToastError(t.errorFixErrors);
            return;
        }

        setIsLoading(true);
        try {
            const validatedZipFile = await validateDrawioZip(selectedFile);

            if ((validatedZipFile.errors ?? []).length > 0) {
                setCurrentErrors(validatedZipFile.errors ?? []);
                setCurrentWarnings(validatedZipFile.warnings ?? []);
                showToastError(t.errorFixErrors);
                return;
            }

            const { token, tenantId, chatbotId } = await resolveActiveScope();
            if (!token || !tenantId || !chatbotId) {
                showToastError(t.drawioMissingUploadContext);
                return;
            }

            if (validatedZipFile.catalog) {
                const indexingSuccessful = await indexCatalogNodes(
                    validatedZipFile.catalog.nodes || [],
                    token,
                    chatbotId,
                    tenantId
                );

                if (!indexingSuccessful) {
                    showToastError(t.errorUploadFailed);
                    return;
                }
            }

            if (validatedZipFile.mediaFiles) {
                const uploadSuccessful = await uploadMediaFiles(
                    validatedZipFile.mediaFiles,
                    token,
                    tenantId,
                    chatbotId
                );

                if (!uploadSuccessful) {
                    showToastError(t.errorMediaUploadFailed);
                    return;
                }
            }

            if ((validatedZipFile.domainIdentifiersWithoutMedia ?? []).length > 0) {
                await deleteMediaInBucket(
                    token,
                    validatedZipFile.domainIdentifiersWithoutMedia,
                    chatbotId,
                    tenantId
                );
            }

            showToastSuccess(t.successUpload);
            clearSelectedFile();
            await loadCatalogStats();
        } catch (error) {
            console.error(error);
            showToastError(t.errorUploadFailed);
        } finally {
            setIsLoading(false);
        }
    };

    async function uploadMediaFiles(mediaFiles, userAuthToken, tenantId, chatbotId) {
        try {
            if (!tenantId || !chatbotId) {
                showToastError(t.drawioMissingUploadContext);
                return false;
            }

            const domainIdentifiersToDelete = Array.from(
                new Set(mediaFiles.map((file) => file.domainNodeIdentifier))
            );

            await deleteMediaInBucket(userAuthToken, domainIdentifiersToDelete, chatbotId, tenantId);

            for (const mediaFile of mediaFiles) {
                const safeDomainIdentifier = String(mediaFile.domainNodeIdentifier || 'node')
                    .normalize('NFKD')
                    .replace(/[^a-zA-Z0-9._-]/g, '_')
                    .replace(/_+/g, '_')
                    .replace(/^[_.]+|[_.]+$/g, '') || 'node';
                const filePath = `tenants/${tenantId}/chatbots/${chatbotId}/drawio/${safeDomainIdentifier}/${mediaFile.fileName}`;

                const fileExtension = mediaFile.fileName.split('.').pop();
                const contentType = fileExtension
                    ? mapExtensionToAllowedContentType(fileExtension)
                    : 'application/octet-stream';

                const { error: uploadError } = await supabase.storage
                    .from(BUCKET_MEDIA_PATH)
                    .upload(filePath, mediaFile.bytes, {
                        contentType: contentType || undefined,
                        upsert: true,
                    });

                if (uploadError) throw uploadError;

                if (mediaFile.description) {
                    const fileNameWithoutExtension = getFileNameWithoutExtension(mediaFile.fileName);
                    const txtPath = `tenants/${tenantId}/chatbots/${chatbotId}/drawio/${safeDomainIdentifier}/${fileNameWithoutExtension}.txt`;

                    const { error: txtError } = await supabase.storage
                        .from(BUCKET_MEDIA_PATH)
                        .upload(txtPath, mediaFile.description, {
                            contentType: 'text/plain',
                            upsert: true,
                        });

                    if (txtError) throw txtError;
                }
            }
        } catch (error) {
            console.error(error);
            return false;
        }

        return true;
    }

    return (
        <Paper
            elevation={0}
            sx={{
                p: { xs: 2.25, md: 3 },
                borderRadius: '28px',
                border: '1px solid var(--border-color)',
                background: 'var(--surface-gradient)',
                boxShadow: '0 24px 48px rgba(15, 23, 42, 0.08)',
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <AccountTree sx={{ color: 'var(--accent-color)' }} />
                <Typography variant="h5" sx={{ fontWeight: 800, color: 'var(--text-strong)' }}>
                    {t.drawioImportTitle}
                </Typography>
            </Box>

            <Typography sx={{ mb: 2.5, color: 'var(--text-muted)', lineHeight: 1.7 }}>
                {t.drawioImportDesc}
                <br />
                <strong>{t.drawioImportNoteLabel}</strong> {t.drawioImportNoteText}
            </Typography>

            <Box sx={{ mb: 3 }}>
                <Paper
                    elevation={0}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        p: { xs: 1.5, md: 2 },
                        borderRadius: '16px',
                        border: '1px solid var(--border-color)',
                        backgroundColor: 'var(--surface-fill-strong)'
                    }}
                >
                    <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'var(--text-strong)' }}>
                            {t.drawioCurrentDatabaseTitle}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'var(--text-muted)', mt: 0.2 }}>
                            {isLoadingStats
                                ? t.loading
                                : tr('drawioCurrentDatabaseCount', { count: catalogStats.nodeCount })}
                        </Typography>
                    </Box>

                    <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        startIcon={isClearing ? <CircularProgress size={16} color="inherit" /> : <DeleteOutline />}
                        onClick={handleClearDrawio}
                        disabled={isClearing || catalogStats.nodeCount === 0 || isLoadingStats}
                        sx={{
                            borderRadius: '12px',
                            textTransform: 'none',
                            fontWeight: 600,
                            backgroundColor: 'rgba(255,255,255,0.5)'
                        }}
                    >
                        {isClearing ? t.clearing : t.clearAll}
                    </Button>
                </Paper>
            </Box>

            <Box width="100%">
                <Paper
                    elevation={0}
                    sx={{
                        p: 1,
                        borderRadius: '24px',
                        background: 'var(--surface-gradient-soft)',
                        border: '2px dashed var(--border-color)',
                    }}
                >
                    <UploadBox
                        value={selectedFile}
                        sx={{ width: '100%', height: '220px', borderRadius: '18px' }}
                        onDropAccepted={onDropAccepted}
                        accept={{ 'application/zip': ['.zip'] }}
                        placeholder={
                            <Box justifyContent="center" display="flex" flexDirection="column" alignItems="center">
                                <Box
                                    sx={{
                                        width: 64,
                                        height: 64,
                                        borderRadius: '20px',
                                        display: 'grid',
                                        placeItems: 'center',
                                        mb: 1,
                                        background:
                                            'linear-gradient(135deg, var(--accent-soft-bg) 0%, var(--accent-soft-bg-2) 100%)',
                                    }}
                                >
                                    <Upload fontSize="large" sx={{ color: 'var(--accent-color)' }} />
                                </Box>

                                <Typography sx={{ color: 'var(--text-soft)', fontWeight: 700 }}>
                                    {t.dropzoneText}
                                </Typography>
                                <Typography sx={{ color: 'var(--text-muted)', fontSize: '0.86rem', mt: 0.4 }}>
                                    {t.drawioZipOnly}
                                </Typography>
                            </Box>
                        }
                    />
                </Paper>

                {selectedFile && (
                    <Box sx={{ marginTop: 2 }}>
                        <Box
                            sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 1,
                                px: 2,
                                py: 1.5,
                                borderRadius: '16px',
                                backgroundColor: 'var(--surface-fill)',
                                border: '1px solid var(--border-color)',
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                {currentErrors.length > 0 ? (
                                    <Cancel sx={{ color: '#dc2626', fontSize: 28 }} />
                                ) : (
                                    <CheckCircle sx={{ color: '#16a34a', fontSize: 28 }} />
                                )}

                                <Box>
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            color: 'var(--text-strong)',
                                            fontWeight: 700,
                                            wordBreak: 'break-word',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1
                                        }}
                                    >
                                        {selectedFile.name}
                                        <Typography
                                            component="span"
                                            variant="caption"
                                            sx={{
                                                color: 'var(--text-muted)',
                                                fontWeight: 600,
                                                px: 0.8,
                                                py: 0.2,
                                                backgroundColor: 'rgba(0,0,0,0.05)',
                                                borderRadius: 1
                                            }}
                                        >
                                            {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                                        </Typography>
                                    </Typography>

                                    {stagedStats && (
                                        <Typography variant="body2" sx={{ color: 'var(--text-soft)', mt: 0.2 }}>
                                            <strong>Overview:</strong> {stagedStats.nodes} interactive node(s),{' '}
                                            {stagedStats.media} media file(s) extracted.
                                        </Typography>
                                    )}

                                    {currentErrors.length > 0 && (
                                        <Typography variant="body2" sx={{ color: '#dc2626', mt: 0.2, fontWeight: 600 }}>
                                            {t.drawioValidationFailed}
                                        </Typography>
                                    )}
                                </Box>
                            </Box>

                            <IconButton
                                onClick={clearSelectedFile}
                                size="small"
                                sx={{
                                    color: 'var(--text-muted)',
                                    '&:hover': {
                                        color: '#dc2626',
                                        backgroundColor: 'rgba(220, 38, 38, 0.08)'
                                    }
                                }}
                            >
                                <Close />
                            </IconButton>
                        </Box>

                        <ErrorBox errors={currentErrors} />

                        {currentWarnings.length > 0 && (
                            <Box
                                sx={{
                                    mt: 1.5,
                                    border: '1px solid rgba(245, 158, 11, 0.38)',
                                    borderRadius: '18px',
                                    px: 2,
                                    py: 1.5,
                                    background:
                                        'linear-gradient(180deg, rgba(245, 158, 11, 0.12) 0%, rgba(245, 158, 11, 0.08) 100%)',
                                }}
                            >
                                <Typography sx={{ color: '#f59e0b', fontWeight: 700, mb: 0.75 }}>
                                    {t.drawioWarningTitle}
                                </Typography>

                                {currentWarnings.map((warning, index) => (
                                    <Typography
                                        key={index}
                                        variant="body2"
                                        sx={{ marginBottom: 0.8, color: '#fde68a', lineHeight: 1.65 }}
                                    >
                                        <strong>{index + 1}.</strong>{' '}
                                        {typeof warning === 'string' ? warning : (warning?.message || String(warning))}
                                    </Typography>
                                ))}

                                <Typography sx={{ mt: 0.5, color: 'var(--text-muted)', fontSize: '0.9rem' }}>
                                    {t.drawioWarningBody}
                                </Typography>
                            </Box>
                        )}

                        <Button
                            sx={{
                                marginTop: 2,
                                minHeight: 54,
                                borderRadius: '16px',
                                fontWeight: 800,
                                textTransform: 'none',
                                background: 'var(--accent-gradient)',
                                boxShadow: '0 16px 32px var(--accent-shadow)',
                                '&.Mui-disabled': {
                                    color: 'rgba(255,255,255,0.72)',
                                    background: 'var(--button-disabled-bg)',
                                },
                            }}
                            variant="contained"
                            fullWidth
                            disabled={currentErrors.length > 0 || isLoading}
                            onClick={handleUpload}
                        >
                            {isLoading ? (
                                <CircularProgress size={24} color="inherit" />
                            ) : currentErrors.length > 0 ? (
                                t.saveImpossible
                            ) : (
                                t.save
                            )}
                        </Button>
                    </Box>
                )}
            </Box>
        </Paper>
    );
}
